import React, { useEffect, useState } from 'react';
import { addCommas, bindClick, cropString } from '@tempu/components/Util';
import { Button, Header, Icon, Label, Message, Placeholder, Stat, Toast, Pagination} from 'react-frontier';
import { MoneySummary, PaymentLog, UserBankAccount } from '../../Classes';
import { useTitle } from '../../Hooks';
import moment from 'moment';
import API from '../../API';
import SelectBankAccountModal from '../../components/SelectBankAccountModal';

interface AccountMoneyStatus{
	// has_accounts: boolean,
	summary: MoneySummary,
	payments: PaymentLog[],
}

const PAGE_SIZE = 5;

var PostingPayment = (props: { payment: AccountMoneyStatus["payments"][0] })=>{
	if(!props.payment){
		return <div className="tp card payment">
			<div className="left">
				<Placeholder height={12} width={20} />
				<Placeholder width={150} variance={100} />
				<Placeholder height={12} width={80} />
				<Placeholder height={12} width={80} />
			</div>
			<div className="right">
				<Placeholder width={40} height={24} />
				<Placeholder height={22} width={50} />
				<Placeholder height={12} width={80} />
			</div>
		</div>
	}
	return <div className="tp card payment">
		<div className="left">
			<div className="meta">Pago #{props.payment.payment_id}</div>
			<div className="title">{cropString(props.payment.posting_name, 32, true)}</div>
			<div className="meta">{props.payment.job_name}</div>
			<div className="meta">{moment.unix(props.payment.date_paid || props.payment.date_created).format('DD/MMM/YY HH:mm')}</div>
		</div>
		<div className="right">
			{props.payment.account_id ? (
				<div className="meta">
					<Icon name={props.payment.clabe ? 'landmark' : 'credit-card'} /> **{props.payment.clabe || props.payment.card_number}
				</div>
			) : (
				<div className="warning">
					<Icon name="exclamation-triangle" /> Sin cuenta
				</div>
			)}
			<div className="amount">
				<span className='sign'>{props.payment.amount>0 ? '+' : ''}</span>
				${addCommas(props.payment.amount)}
			</div>
			<Label 
				size='small' 
				color={props.payment.paid ? 'green' : (props.payment.account_id ? 'orange' : 'blue')}
			>
				{props.payment.paid ? 'Pagado' : (props.payment.account_id ? 'Procesando' : 'Pendiente')}
			</Label>
		</div>
	</div>
}

var AccountMoney = ()=>{
	var { setTitle } = useTitle();
	var [loadError, setLoadError] = useState<string>(null);
	var [data, setData] = useState<AccountMoneyStatus>(null);
	var [selectModal, setSelectModal] = useState<boolean>(false);
	var [sending, setSending] = useState<boolean>(false);
	var [page, setPage] = useState<number>(0);
	var [loading, setLoading] = useState<boolean>(false);

	var getSummary = (get_page?: number)=>{
		var offset = (get_page > 0) ? Math.max(0, (get_page || page) * PAGE_SIZE) : 0;
		
		setLoading(true);
		API.getMoneySummary(offset, PAGE_SIZE).then(res=>{
			if(res.error) return setLoadError(res.message);
			setData(res.data);
		}).catch(err=>{
			setLoadError('Hubo un error inesperado cargando tu estado de cuenta (LCL-ACMNY-1)');
		}).finally(()=>{
			setLoading(false);
		})
	}

	var onPageChange = (page: number)=>{
		setPage(page);
		getSummary(page);
	}

	useEffect(()=>{
		setTitle('Mi dinero');
		getSummary();
	}, []);

	var onAccountSelected = (account: UserBankAccount)=>{
		if(!account) return Toast.error('Favor de seleccionar una cuenta bancaria');
		setSelectModal(false);
		setSending(true);
		var tid = Toast.show('Modificando liquidaciones...', { loading: true });
		API.setPaymentBankAccount(account.account_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			if(res.data && res.data.payments){
				if(res.data.payments.length==0){
					return Toast.error('No hay pagos con información de transferencia pendiente');
				}
				Toast.success('Se ha agregado la información de transferencia a las liquidaciones', { duration: 10000 });
				setData(null);
				getSummary();
			}else{
				return Toast.success('Hubo un error modificando la información de transferencia de las liquidaciones (LCL-ACMPE-2)', { duration: 10000 });
			}
		}).catch(err=>{
			return Toast.success('Hubo un error modificando la información de transferencia de las liquidaciones (LCL-ACMPE-1)', { duration: 10000 });
		}).finally(()=>{
			Toast.dismiss(tid);
			setSending(false);
		});
	}
	
	if(loadError){
		return <Header text={'Error'} subtext={loadError} iconName='face-frown-open' />
	}

	return <div className="tp account-money-screen">
		<Stat.Group>
			<Stat valueStyle={{ color: data && data.summary.total_pending==0 ? '#CCC' : 'black' }} value={data ? `$${addCommas(data.summary.total_pending)}` : (
				<Placeholder height={35} />
			)} label={'En proceso'} />
			<Stat valueStyle={{ color: data && data.summary.total_available==0 ? '#CCC' : 'black' }} value={data? `$${addCommas(data.summary.total_held)}` : (
				<Placeholder height={35} />
			)} label={'Saldo a favor'} />
		</Stat.Group>
		{!!data && data.summary?.total_held>0 && (
			<Message type='info' style={{ marginTop: 15 }} centered header='Saldo a favor'>
				Tienes un saldo a favor de <b>${addCommas(data.summary.total_held)}</b>, para poder liquidarlo, tendrás que seleccionar a que cuenta bancaria deseas depositarlo.
				<br />
				<Button color='blue' text='Seleccionar cuenta' disabled={sending} style={{ marginTop: 5, width: 200 }} onClick={bindClick(setSelectModal, true)} />
			</Message>
		)}
		<Header text={'Pagos'} style={{ textAlign: 'left', marginTop: 15 }} />
		{data && !loading ? <>
			{data.payments.length===0 ? (
				null
			) : data.payments.map(a=>(
				<PostingPayment payment={a} key={`PPLH-${a.payment_id}`} />
			))}
		</> : (
			new Array(5).fill(0).map((a, i)=>(
				<PostingPayment payment={null} key={`PPLH-${i}`} />
			))
		)}
		{data && data.payments.length === 0 && <Header text='No tienes dinero activo.' centered style={{ fontWeight: 'bold', marginTop: 15 }} />}
		{data && data.payments.length >= PAGE_SIZE || page !== 0 ? <div style={{ textAlign: 'center' }}>
			<Pagination page={page} onPageChange={onPageChange} />
		</div> : null }
		<SelectBankAccountModal
			open={selectModal}
			onClose={bindClick(setSelectModal, false)}
			onSelected={onAccountSelected}
		/>
	</div>
}

export default AccountMoney;